import { useState, useEffect } from 'react';
import { Paper, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Radio, CircularProgress, Alert, Grid, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import AddHotTopicModal from './AddHotTopicModal';
import ModifyHotTopicModal from './ModifyHotTopicModal';
import { getHotTopics, deleteHotTopic, createHotTopic, updateHotTopic } from '../../services/adminApiClient';

// Helper to format dates to PDT
const formatToPDT = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
        timeZone: 'America/Los_Angeles', year: 'numeric', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true
    });
};

export default function HotTopicsTab({ customers, loading: customersLoading, error: customersError }) {
    const [countryCode, setCountryCode] = useState('US'); // NEW
    const [allHotTopics, setAllHotTopics] = useState({});
    const [displayedData, setDisplayedData] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [isAddModalOpen, setAddModalOpen] = useState(false);
    const [isModifyModalOpen, setModifyModalOpen] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [error, setError] = useState('');
    const [actionStatus, setActionStatus] = useState({ type: '', text: '' });

    const fetchData = async () => {
        setLoadingData(true);
        setError('');
        try {
            const response = await getHotTopics();
            if (response.success && Array.isArray(response.data)) {
                const groupedData = response.data.reduce((acc, item) => {
                    const key = item.client_code;
                    if (!acc[key]) acc[key] = [];
                    acc[key].push({
                        id: item.hot_topic_id,
                        clientCode: item.client_code,
                        clientName: item.client_name,
                        content: item.content,
                        validFrom: item.valid_from,
                        validTill: item.valid_till,
                        timestamp: item.created_at,
                        createdBy: item.last_updated_by || 'N/A'
                    });
                    return acc;
                }, {});
                setAllHotTopics(groupedData);
            } else {
                throw new Error("Invalid data format received from API.");
            }
        } catch (e) {
            setError(e.message || "Failed to fetch hot topics.");
        } finally {
            setLoadingData(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    useEffect(() => {
        if (selectedCustomer && selectedCustomer.ClientCode) {
            setDisplayedData(allHotTopics[selectedCustomer.ClientCode] || []);
            setSelectedRowId(null);
        } else {
            setDisplayedData([]);
        }
    }, [selectedCustomer, allHotTopics]);

    const handleCustomerChange = (event) => {
        const cust = filteredCustomers.find(c => c.ClientCode === event.target.value);
        setSelectedCustomer(cust);
    };

    const handleCountryChange = (event) => {
        const newCountry = event.target.value;
        setCountryCode(newCountry);
        setSelectedCustomer(null);
    };

    const handleDelete = async () => {
        if (!selectedRowId || !window.confirm(`Are you sure you want to delete this hot topic?`)) return;
        try {
            await deleteHotTopic({ hottopic_id: selectedRowId });
            setActionStatus({ type: 'success', text: 'Hot topic deleted successfully!' });
            fetchData();
        } catch (e) {
            setActionStatus({ type: 'error', text: e.message || 'Failed to delete hot topic.' });
        }
    };

    const handleAddSubmit = async (payload) => {
        try {
            await createHotTopic(payload);
            setAddModalOpen(false);
            fetchData();
            setActionStatus({ type: 'success', text: 'Hot Topic added successfully!' });
        } catch (e) {
            throw e;
        }
    };

    const handleModifySubmit = async (payload) => {
        try {
            await updateHotTopic(payload);
            setModifyModalOpen(false);
            fetchData();
            setActionStatus({ type: 'success', text: 'Hot Topic updated successfully!' });
        } catch (e) {
            throw e;
        }
    };

    const filteredCustomers = customers.filter(c => c.region === countryCode);

    if (customersLoading) return <CircularProgress />;
    if (customersError) return <Alert severity="error">Error loading customers: {customersError}</Alert>;

    return (
        <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Hot Topics</Typography>
            {actionStatus.text && <Alert severity={actionStatus.type} sx={{ mb: 2 }}>{actionStatus.text}</Alert>}
            <Grid container spacing={2} alignItems="center">
                {/* Country Dropdown */}
                <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                        <InputLabel>COUNTRY</InputLabel>
                        <Select value={countryCode} label="COUNTRY" onChange={handleCountryChange}>
                            <MenuItem value="US">US</MenuItem>
                            <MenuItem value="CA">CA</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                {/* Customer Dropdown */}
                <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                        <InputLabel>CUSTOMERS</InputLabel>
                        <Select
                            value={selectedCustomer ? selectedCustomer.ClientCode : ''}
                            label="CUSTOMERS"
                            onChange={handleCustomerChange}
                        >
                            {filteredCustomers.map(customer => (
                                <MenuItem key={customer.ClientCode} value={customer.ClientCode}>
                                    {customer.ClientName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button variant="outlined" onClick={() => setAddModalOpen(true)} disabled={!selectedCustomer}>Add New</Button>
                    <Button variant="outlined" onClick={() => setModifyModalOpen(true)} disabled={!selectedRowId}>Modify</Button>
                    <Button variant="contained" color="error" onClick={handleDelete} disabled={!selectedRowId}>Delete</Button>
                </Grid>
            </Grid>

            {loadingData && <CircularProgress sx={{ mt: 2 }} />}
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

            {!loadingData && !error && (
                <TableContainer component={Paper} sx={{ mt: 3, border: '1px solid #ddd' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                                <TableCell padding="checkbox"></TableCell>
                                <TableCell>Hot Topic ID</TableCell>
                                <TableCell>Content</TableCell>
                                <TableCell>Timestamp</TableCell>
                                <TableCell>Valid From (PDT)</TableCell>
                                <TableCell>Valid Till (PDT)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayedData.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell padding="checkbox">
                                        <Radio checked={selectedRowId === row.id} onChange={() => setSelectedRowId(row.id)} value={row.id} />
                                    </TableCell>
                                    <TableCell sx={{ wordBreak: 'break-all' }}>{row.id}</TableCell>
                                    <TableCell sx={{ wordBreak: 'break-word' }}>
                                        <ReactMarkdown linkTarget="_blank">
                                            {row.content || ''}
                                        </ReactMarkdown>
                                    </TableCell>
                                    <TableCell>{formatToPDT(row.timestamp)}</TableCell>
                                    <TableCell>{formatToPDT(row.validFrom)}</TableCell>
                                    <TableCell>{formatToPDT(row.validTill)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <AddHotTopicModal
                open={isAddModalOpen}
                onClose={() => setAddModalOpen(false)}
                customer={selectedCustomer}
                onSubmit={handleAddSubmit}
            />
            <ModifyHotTopicModal
                open={isModifyModalOpen}
                onClose={() => setModifyModalOpen(false)}
                customer={selectedCustomer}
                hotTopic={displayedData.find(d => d.id === selectedRowId)}
                onSubmit={handleModifySubmit}
            />
        </Paper>
    );
}
